import { useAppDataService } from "../composables/useAppDataFrontendService";
import type { AppDataFrontendService } from "../services/AppDataFrontendService";
import { useAuthenticationService } from "../composables/useAuthenticationFrontendService";
import type { AuthenticationFrontendService } from "../services/AuthenticationFrontendService";
import { useCalendarService } from "../composables/useCalendarFrontendService";
import type { CalendarFrontendService } from "../services/CalendarFrontendService";
import { useCatalogService } from "../composables/useCatalogFrontendService";
import type { CatalogFrontendService } from "../services/CatalogFrontendService";
import { useProgramTypeService } from "../composables/useProgramTypeFrontendService";
import type { ProgramTypeFrontendService } from "../services/ProgramTypeFrontendService";
import { useClassLocationService } from "../composables/useClassLocationFrontendService";
import type { ClassLocationFrontendService } from "../services/ClassLocationFrontendService";
import { useApClassService } from "../composables/useApClassFrontendService";
import type { ApClassFrontendService } from "../services/ApClassFrontendService";
import { useJpClassService } from "../composables/useJpClassFrontendService";
import type { JpClassFrontendService } from "../services/JpClassFrontendService";
import { useClassUnifiedService } from "../composables/useClassUnifiedFrontendService";
import type { ClassUnifiedFrontendService } from "../services/ClassUnifiedFrontendService";
import { useEiiService } from "../composables/useEiiFrontendService";
import type { EiiFrontendService } from "../services/EiiFrontendService";
import { useEmailTrackingService } from "../composables/useEmailTrackingFrontendService";
import type { EmailTrackingFrontendService } from "../services/EmailTrackingFrontendService";
import { useJobHealthService } from "../composables/useJobHealthFrontendService";
import type { JobHealthFrontendService } from "../services/JobHealthFrontendService";
import { useMemberService } from "../composables/useMemberFrontendService";
import type { MemberFrontendService } from "../services/MemberFrontendService";
import { useMembershipService } from "../composables/useMembershipFrontendService";
import type { MembershipFrontendService } from "../services/MembershipFrontendService";
import { useGuestPrivilegeService } from "../composables/useGuestPrivilegeFrontendService";
import type { GuestPrivilegeFrontendService } from "../services/GuestPrivilegeFrontendService";
import { useOptOutService } from "../composables/useOptOutFrontendService";
import type { OptOutFrontendService } from "../services/OptOutFrontendService";
import { useAddressService } from "../composables/useAddressFrontendService";
import type { AddressFrontendService } from "../services/AddressFrontendService";
import { usePhoneService } from "../composables/usePhoneFrontendService";
import type { PhoneFrontendService } from "../services/PhoneFrontendService";
import { useCardService } from "../composables/useCardFrontendService";
import type { CardFrontendService } from "../services/CardFrontendService";
import { usePersonService } from "../composables/usePersonFrontendService";
import type { PersonFrontendService } from "../services/PersonFrontendService";
import { usePersonTypeRulesService } from "../composables/usePersonTypeRulesFrontendService";
import type { PersonTypeRulesFrontendService } from "../services/PersonTypeRulesFrontendService";
import { usePurchaseDeliveryService } from "../composables/usePurchaseDeliveryFrontendService";
import type { PurchaseDeliveryFrontendService } from "../services/PurchaseDeliveryFrontendService";
import { useRatingService } from "../composables/useRatingFrontendService";
import type { RatingFrontendService } from "../services/RatingFrontendService";
import { useStripeService } from "../composables/useStripeFrontendService";
import type { StripeFrontendService } from "../services/StripeFrontendService";
import { useBalanceService } from "../composables/useBalanceFrontendService";
import type { BalanceFrontendService } from "../services/BalanceFrontendService";
import { useCacheService } from "../composables/useCacheFrontendService";
import type { CacheFrontendService } from "../services/CacheFrontendService";
import { useCashDrawerService } from "../composables/useCashDrawerFrontendService";
import type { CashDrawerFrontendService } from "../services/CashDrawerFrontendService";
import { useDamageWaiverService } from "../composables/useDamageWaiverFrontendService";
import type { DamageWaiverFrontendService } from "../services/DamageWaiverFrontendService";
import { useDiscountService } from "../composables/useDiscountFrontendService";
import type { DiscountFrontendService } from "../services/DiscountFrontendService";
import { useDonationService } from "../composables/useDonationFrontendService";
import type { DonationFrontendService } from "../services/DonationFrontendService";
import { useOrderService } from "../composables/useOrderFrontendService";
import type { OrderFrontendService } from "../services/OrderFrontendService";
import { useOrderRefundService } from "../composables/useOrderRefundFrontendService";
import type { OrderRefundFrontendService } from "../services/OrderRefundFrontendService";
import { usePaymentService } from "../composables/usePaymentFrontendService";
import type { PaymentFrontendService } from "../services/PaymentFrontendService";
import { usePermissionService } from "../composables/usePermissionFrontendService";
import type { PermissionFrontendService } from "../services/PermissionFrontendService";
import { usePersonCheckInService } from "../composables/usePersonCheckInFrontendService";
import type { PersonCheckInFrontendService } from "../services/PersonCheckInFrontendService";
import { usePrinterService } from "../composables/usePrinterFrontendService";
import type { PrinterFrontendService } from "../services/PrinterFrontendService";
import { useReportService } from "../composables/useReportFrontendService";
import type { ReportFrontendService } from "../services/ReportFrontendService";
import { useStockService } from "../composables/useStockFrontendService";
import type { StockFrontendService } from "../services/StockFrontendService";
import { useStripeReportService } from "../composables/useStripeReportFrontendService";
import type { StripeReportFrontendService } from "../services/StripeReportFrontendService";
import { useTaxService } from "../composables/useTaxFrontendService";
import type { TaxFrontendService } from "../services/TaxFrontendService";
import { useTestResourceService } from "../composables/useTestResourceFrontendService";
import type { TestResourceFrontendService } from "../services/TestResourceFrontendService";
import { useUserService } from "../composables/useUserFrontendService";
import type { UserFrontendService } from "../services/UserFrontendService";

/**
 * Per-DTO-instance context object exposing every frontend service, so lazy relation methods
 * generated on a DTO can resolve the service they need without a bespoke loader function.
 */
export interface CbiFrontendServiceContext {
  appDataService(): AppDataFrontendService;
  authenticationService(): AuthenticationFrontendService;
  calendarService(): CalendarFrontendService;
  catalogService(): CatalogFrontendService;
  programTypeService(): ProgramTypeFrontendService;
  classLocationService(): ClassLocationFrontendService;
  apClassService(): ApClassFrontendService;
  jpClassService(): JpClassFrontendService;
  classUnifiedService(): ClassUnifiedFrontendService;
  eiiService(): EiiFrontendService;
  emailTrackingService(): EmailTrackingFrontendService;
  jobHealthService(): JobHealthFrontendService;
  memberService(): MemberFrontendService;
  membershipService(): MembershipFrontendService;
  guestPrivilegeService(): GuestPrivilegeFrontendService;
  optOutService(): OptOutFrontendService;
  addressService(): AddressFrontendService;
  phoneService(): PhoneFrontendService;
  cardService(): CardFrontendService;
  personService(): PersonFrontendService;
  personTypeRulesService(): PersonTypeRulesFrontendService;
  purchaseDeliveryService(): PurchaseDeliveryFrontendService;
  ratingService(): RatingFrontendService;
  stripeService(): StripeFrontendService;
  balanceService(): BalanceFrontendService;
  cacheService(): CacheFrontendService;
  cashDrawerService(): CashDrawerFrontendService;
  damageWaiverService(): DamageWaiverFrontendService;
  discountService(): DiscountFrontendService;
  donationService(): DonationFrontendService;
  orderService(): OrderFrontendService;
  orderRefundService(): OrderRefundFrontendService;
  paymentService(): PaymentFrontendService;
  permissionService(): PermissionFrontendService;
  personCheckInService(): PersonCheckInFrontendService;
  printerService(): PrinterFrontendService;
  reportService(): ReportFrontendService;
  stockService(): StockFrontendService;
  stripeReportService(): StripeReportFrontendService;
  taxService(): TaxFrontendService;
  testResourceService(): TestResourceFrontendService;
  userService(): UserFrontendService;
}

export const cbiFrontendServiceContext: CbiFrontendServiceContext = {
  appDataService: () => useAppDataService(),
  authenticationService: () => useAuthenticationService(),
  calendarService: () => useCalendarService(),
  catalogService: () => useCatalogService(),
  programTypeService: () => useProgramTypeService(),
  classLocationService: () => useClassLocationService(),
  apClassService: () => useApClassService(),
  jpClassService: () => useJpClassService(),
  classUnifiedService: () => useClassUnifiedService(),
  eiiService: () => useEiiService(),
  emailTrackingService: () => useEmailTrackingService(),
  jobHealthService: () => useJobHealthService(),
  memberService: () => useMemberService(),
  membershipService: () => useMembershipService(),
  guestPrivilegeService: () => useGuestPrivilegeService(),
  optOutService: () => useOptOutService(),
  addressService: () => useAddressService(),
  phoneService: () => usePhoneService(),
  cardService: () => useCardService(),
  personService: () => usePersonService(),
  personTypeRulesService: () => usePersonTypeRulesService(),
  purchaseDeliveryService: () => usePurchaseDeliveryService(),
  ratingService: () => useRatingService(),
  stripeService: () => useStripeService(),
  balanceService: () => useBalanceService(),
  cacheService: () => useCacheService(),
  cashDrawerService: () => useCashDrawerService(),
  damageWaiverService: () => useDamageWaiverService(),
  discountService: () => useDiscountService(),
  donationService: () => useDonationService(),
  orderService: () => useOrderService(),
  orderRefundService: () => useOrderRefundService(),
  paymentService: () => usePaymentService(),
  permissionService: () => usePermissionService(),
  personCheckInService: () => usePersonCheckInService(),
  printerService: () => usePrinterService(),
  reportService: () => useReportService(),
  stockService: () => useStockService(),
  stripeReportService: () => useStripeReportService(),
  taxService: () => useTaxService(),
  testResourceService: () => useTestResourceService(),
  userService: () => useUserService(),
};
